<script setup lang="ts">
import { BookWithDuplicates, useBooks } from '@ilteducation/books';
import BookCoverFeatures from '@/components/bookshelf/BookCoverFeatures.vue';
import BookCoverPlayButton from '@/components/bookshelf/BookCoverPlayButton.vue';
import useGuiStore from '@/store/gui';
import { computed } from 'vue';
import type { Category } from '@ilteducation/content-ref-data-types';
import { useRoute } from 'vue-router';
import { useCategories } from '@ilteducation/content-ref-data';
import { ContentCoverStack, ContentHoverCard, useCoverDimensions } from '@ilteducation/components';

const props = withDefaults(
  defineProps<{
    book: BookWithDuplicates;
    playable?: boolean;
    showAsStack?: boolean;
    zoomOnHover?: boolean;
    maxWidth?: number;
  }>(),
  {
    playable: false,
    showAsStack: false,
    zoomOnHover: true,
    maxWidth: 200,
  },
);

const emit = defineEmits<{
  (e: 'onBookClick', book: BookWithDuplicates): void;
}>();

const { getCoverUrl } = useBooks();
const route = useRoute();
const { categories } = useCategories();

const getBookManabuaSubject = (book: BookWithDuplicates): Category | undefined => {
  const bookCategories = book.aggregatedCategories || book.categories;
  if (route.params?.subject) {
    const subjectFromRoute = categories.value.find((c) => c.slug === route.params.subject);
    if (subjectFromRoute && bookCategories?.includes(subjectFromRoute?.id)) {
      return subjectFromRoute;
    }
  }
  if (route.query?.subject) {
    const subjectFromQuery = categories.value.find((c) => c.id === route.query.subject);
    if (subjectFromQuery && bookCategories?.includes(subjectFromQuery?.id)) {
      return subjectFromQuery;
    }
  }
  return categories.value.find(
    (c) =>
      c.kind === 'subject' &&
      c.subscriptionTypes?.some((st) => st.startsWith('immigrant') && bookCategories?.includes(c.id)),
  );
};

const maxHeight = 200;
const { coverRef, coverHeight, coverWidth, coverCardWidth, coverCardHeight, onImageLoaded } = useCoverDimensions(
  120,
  maxHeight,
);

const bookSubject = computed(() => getBookManabuaSubject(props.book));
const { getThemeName } = useGuiStore();
</script>

<template>
  <ContentHoverCard
    v-if="props.zoomOnHover"
    :content-card-width="coverCardWidth"
    :content-card-height="coverCardHeight"
    :image-height="coverHeight"
    :has-stack="showAsStack"
    :data-book-id="book.id"
    :overflow-hidden="false"
    :is-rounded="true"
    class="justify-end"
    hover-rounded-class="rounded-sm"
    @on-card-click="emit('onBookClick', book)"
  >
    <template #coverFeatures>
      <BookCoverFeatures :book="book" :show-as-stack="showAsStack" :bookSubject="bookSubject"> </BookCoverFeatures>
    </template>
    <template #image>
      <v-btn tabindex="0" variant="text" :width="coverWidth" :height="coverHeight" :data-testid="`book-${book.id}`">
        <v-img
          ref="coverRef"
          class="bg-surface-container-low elevation-4 rounded"
          :width="coverWidth"
          :height="coverHeight"
          :src="getCoverUrl(book.id)"
          :alt="book.title"
          cover
          @load="onImageLoaded(maxWidth, maxHeight)"
        >
        </v-img>
      </v-btn>
      <BookCoverPlayButton v-if="playable" :book="book" :max-width="maxWidth"> </BookCoverPlayButton>
    </template>
    <template v-if="showAsStack && coverWidth && coverHeight" #stack>
      <ContentCoverStack
        :width="coverWidth"
        :height="coverHeight"
        :src="getCoverUrl(book.id)"
        :level="book.duplicatesCount || 0"
        :alt="book.title"
        aria-hidden="true"
      >
      </ContentCoverStack>
    </template>
  </ContentHoverCard>
  <v-sheet
    v-else
    :max-width="maxWidth"
    :theme="getThemeName(bookSubject?.slug)"
    color="transparent"
    class="position-relative cursor-pointer w-100"
  >
    <BookCoverFeatures :book="book" :show-as-stack="showAsStack" :bookSubject="bookSubject"> </BookCoverFeatures>
    <v-img
      :max-width="maxWidth"
      style="z-index: 3"
      class="elevation-10 rounded position-relative"
      contain
      :src="getCoverUrl(book.id)"
      alt=""
    >
    </v-img>
    <BookCoverPlayButton v-if="playable" :book="book" :max-width="maxWidth"> </BookCoverPlayButton>
  </v-sheet>
</template>

<style scoped lang="scss"></style>
